import React, { useRef, useMemo } from 'react';
import { GRID_SIZE, RULER_SIZE, HANDLE_SIZE, TOOLS, ARROW_STYLES, getBBox, getConnectorPath } from './editorHelpers';
import { generateFilterSVG } from './advancedFeatures';

const EditorCanvas = ({
  objects, selectedIds, tool, zoom, panX, panY, gridEnabled, snapEnabled, showRulers,
  penPoints, currentStyle, snapGuides, rubberBand, lassoPoints,
  clipMasks, directSelectPoints, directSelectIdx,
  anchorPoints, anchorSelectedIdx, onAnchorClick, onAnchorHandleDrag, onAnchorPointDrag,
  onMouseDown, onMouseMove, onMouseUp, onCanvasClick, onObjectMouseDown,
  onWheel, onContextMenu, canvasW, canvasH
}) => {
  const svgRef = useRef(null);

  const viewBox = useMemo(() => {
    const vw = canvasW / zoom;
    const vh = canvasH / zoom;
    return `${-panX / zoom} ${-panY / zoom} ${vw} ${vh}`;
  }, [canvasW, canvasH, zoom, panX, panY]);

  // Collect all gradient definitions
  const gradientDefs = useMemo(() => {
    const defs = [];
    objects.forEach(obj => {
      if (obj.gradient) {
        const g = obj.gradient;
        if (g.type === 'linear') {
          defs.push(
            <linearGradient key={g.id} id={g.id} x1={`${g.x1}%`} y1={`${g.y1}%`} x2={`${g.x2}%`} y2={`${g.y2}%`}>
              {(g.stops || []).map((s, i) => (
                <stop key={i} offset={`${s.offset * 100}%`} stopColor={s.color} stopOpacity={s.opacity ?? 1} />
              ))}
            </linearGradient>
          );
        } else if (g.type === 'radial') {
          defs.push(
            <radialGradient key={g.id} id={g.id} cx={`${g.cx}%`} cy={`${g.cy}%`} r={`${g.r}%`}>
              {(g.stops || []).map((s, i) => (
                <stop key={i} offset={`${s.offset * 100}%`} stopColor={s.color} stopOpacity={s.opacity ?? 1} />
              ))}
            </radialGradient>
          );
        }
      }
      // Patterns
      if (obj.pattern) {
        const p = obj.pattern;
        defs.push(
          <pattern key={p.id} id={p.id} width={p.tileW} height={p.tileH} patternUnits="userSpaceOnUse">
            <g style={{ color: obj.fill || '#0D9488' }} dangerouslySetInnerHTML={{ __html: p.content }} />
          </pattern>
        );
      }
    });
    return defs;
  }, [objects]);

  // Collect filter definitions
  const filterDefs = useMemo(() => {
    const defs = [];
    objects.forEach(obj => {
      if (obj.effects && obj.effects.length > 0) {
        const filterId = `filter-${obj.id}`;
        const svg = generateFilterSVG(obj.effects, filterId);
        if (svg) defs.push(<g key={filterId} dangerouslySetInnerHTML={{ __html: svg }} />);
      }
    });
    return defs;
  }, [objects]);

  // Clip mask definitions
  const clipDefs = useMemo(() => {
    if (!clipMasks || clipMasks.length === 0) return [];
    return clipMasks.map(cm => {
      const maskObj = objects.find(o => o.id === cm.maskId);
      if (!maskObj) return null;
      return (
        <clipPath key={cm.clipPathId} id={cm.clipPathId}>
          {maskObj.type === 'rect' && <rect x={maskObj.x} y={maskObj.y} width={maskObj.w} height={maskObj.h} rx={maskObj.rx || 0} />}
          {maskObj.type === 'ellipse' && <ellipse cx={maskObj.x + maskObj.w / 2} cy={maskObj.y + maskObj.h / 2} rx={maskObj.w / 2} ry={maskObj.h / 2} />}
          {maskObj.type === 'path' && <path d={maskObj.d} />}
        </clipPath>
      );
    }).filter(Boolean);
  }, [clipMasks, objects]);

  const renderGrid = () => {
    if (!gridEnabled) return null;
    const gs = GRID_SIZE;
    return (
      <g opacity="0.4">
        <defs>
          <pattern id="grid-minor" width={gs} height={gs} patternUnits="userSpaceOnUse">
            <circle cx={gs / 2} cy={gs / 2} r="0.6" fill="#CBD5E1" />
          </pattern>
          <pattern id="grid-major" width={gs * 5} height={gs * 5} patternUnits="userSpaceOnUse">
            <rect width={gs * 5} height={gs * 5} fill="url(#grid-minor)" />
            <circle cx={0} cy={0} r="1" fill="#94A3B8" />
          </pattern>
        </defs>
        <rect x={-5000} y={-5000} width={10000} height={10000} fill="url(#grid-major)" />
      </g>
    );
  };

  const renderRulers = () => {
    if (!showRulers) return null;
    const vx = -panX / zoom;
    const vy = -panY / zoom;
    const vw = canvasW / zoom;
    const vh = canvasH / zoom;
    const step = zoom > 2 ? 10 : zoom > 0.5 ? 20 : zoom > 0.2 ? 50 : 100;
    const ticks = [];
    const rh = RULER_SIZE / zoom;
    const startX = Math.floor(vx / step) * step;
    for (let x = startX; x < vx + vw; x += step) {
      const isMajor = x % (step * 5) === 0;
      ticks.push(<line key={`hx-${x}`} x1={x} y1={vy} x2={x} y2={vy + (isMajor ? rh : rh * 0.5)} stroke="#94A3B8" strokeWidth={0.5 / zoom} />);
      if (isMajor) ticks.push(<text key={`ht-${x}`} x={x + 2 / zoom} y={vy + rh * 0.75} fontSize={9 / zoom} fill="#64748B" fontFamily="IBM Plex Mono">{Math.round(x)}</text>);
    }
    const startY = Math.floor(vy / step) * step;
    for (let y = startY; y < vy + vh; y += step) {
      const isMajor = y % (step * 5) === 0;
      ticks.push(<line key={`vy-${y}`} x1={vx} y1={y} x2={vx + (isMajor ? rh : rh * 0.5)} y2={y} stroke="#94A3B8" strokeWidth={0.5 / zoom} />);
      if (isMajor) ticks.push(<text key={`vt-${y}`} x={vx + 2 / zoom} y={y - 2 / zoom} fontSize={9 / zoom} fill="#64748B" fontFamily="IBM Plex Mono" transform={`rotate(-90, ${vx + 2 / zoom}, ${y - 2 / zoom})`}>{Math.round(y)}</text>);
    }
    return <g>{ticks}</g>;
  };

  const renderArrowMarker = (obj) => {
    const arrowEnd = obj.arrowEnd || 'triangle';
    const arrowDef = ARROW_STYLES.find(a => a.id === arrowEnd) || ARROW_STYLES[0];
    const mid = `marker-${obj.id}`;
    return (
      <marker key={mid} id={mid} markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto-start-reverse">
        <path d={arrowDef.path} fill={arrowDef.fill === 'none' ? 'none' : obj.stroke} stroke={arrowDef.fill === 'none' ? obj.stroke : 'none'} strokeWidth="1.5" />
      </marker>
    );
  };

  const getFillValue = (obj) => {
    if (obj.gradient) return `url(#${obj.gradient.id})`;
    if (obj.pattern) return `url(#${obj.pattern.id})`;
    return obj.fill || 'none';
  };

  const getFilterAttr = (obj) => {
    if (obj.effects && obj.effects.length > 0) return `url(#filter-${obj.id})`;
    return undefined;
  };

  const getClipAttr = (obj) => {
    if (!clipMasks) return undefined;
    const cm = clipMasks.find(c => c.targetId === obj.id);
    return cm ? `url(#${cm.clipPathId})` : undefined;
  };

  const renderObject = (obj) => {
    if (obj.visible === false) return null;
    const isSelected = selectedIds.includes(obj.id);
    const op = obj.opacity ?? 1;
    const dash = obj.dashStyle || undefined;
    const cap = obj.lineCap || 'round';
    const join = obj.lineJoin || 'round';
    const cursor = (tool === TOOLS.SELECT || tool === TOOLS.DIRECT_SELECT || tool === TOOLS.ANCHOR_POINT) && !obj.locked ? 'move' : 'default';
    const blendMode = obj.blendMode || 'normal';
    const filter = getFilterAttr(obj);
    const clipPath = getClipAttr(obj);
    const fill = getFillValue(obj);

    const common = {
      onMouseDown: (e) => onObjectMouseDown(e, obj.id),
      style: { cursor, opacity: op, mixBlendMode: blendMode !== 'normal' ? blendMode : undefined },
    };

    let element = null;

    switch (obj.type) {
      case 'rect':
        element = (
          <rect x={obj.x} y={obj.y} width={obj.w} height={obj.h}
            fill={fill} stroke={obj.stroke || 'none'} strokeWidth={obj.strokeWidth || 0}
            rx={obj.rx || 0} strokeDasharray={dash} strokeLinecap={cap} strokeLinejoin={join}
            filter={filter} clipPath={clipPath}
            transform={obj.rotation ? `rotate(${obj.rotation} ${obj.x + obj.w / 2} ${obj.y + obj.h / 2})` : undefined}
            {...common} />
        );
        break;
      case 'ellipse':
        element = (
          <ellipse cx={obj.x + obj.w / 2} cy={obj.y + obj.h / 2} rx={obj.w / 2} ry={obj.h / 2}
            fill={fill} stroke={obj.stroke || 'none'} strokeWidth={obj.strokeWidth || 0}
            strokeDasharray={dash} strokeLinejoin={join}
            filter={filter} clipPath={clipPath}
            transform={obj.rotation ? `rotate(${obj.rotation} ${obj.x + obj.w / 2} ${obj.y + obj.h / 2})` : undefined}
            {...common} />
        );
        break;
      case 'text': {
        const lines = (obj.text || 'Text').split('\n');
        const fs = obj.fontSize || 16;
        const lh = obj.lineHeight || 1.4;
        const anchor = obj.textAlign === 'center' ? 'middle' : obj.textAlign === 'right' ? 'end' : 'start';
        if (obj.textPathId) {
          element = (
            <g {...common} filter={filter}>
              <text fontSize={fs} fill={obj.fill || '#0B1D3A'} fontFamily={obj.fontFamily || 'IBM Plex Sans'}
                fontWeight={obj.fontWeight || 'normal'} fontStyle={obj.fontStyle || 'normal'}>
                <textPath href={`#textpath-${obj.textPathId}`} startOffset={obj.textPathOffset || '0%'}>
                  {obj.text || 'Text on path'}
                </textPath>
              </text>
            </g>
          );
        } else {
          element = (
            <g {...common} filter={filter}>
              {lines.map((line, i) => (
                <text key={i} x={obj.x} y={obj.y + fs * (i + 0.85) * lh}
                  fontSize={fs} fill={obj.fill || '#0B1D3A'}
                  fontFamily={obj.fontFamily || 'IBM Plex Sans'}
                  fontWeight={obj.fontWeight || 'normal'}
                  fontStyle={obj.fontStyle || 'normal'}
                  textAnchor={anchor}
                  letterSpacing={obj.letterSpacing || 0}
                  style={{ baselineShift: obj.baselineShift || 'auto' }}>
                  {line}
                </text>
              ))}
              <rect x={obj.x - 4} y={obj.y} width={(obj.w || 100) + 8} height={lines.length * fs * lh + 4}
                fill="transparent" stroke="none" />
            </g>
          );
        }
        break;
      }
      case 'line':
        element = (
          <g {...common}>
            <line x1={obj.x1} y1={obj.y1} x2={obj.x2} y2={obj.y2}
              stroke={obj.stroke} strokeWidth={obj.strokeWidth} strokeDasharray={dash} strokeLinecap={cap} strokeLinejoin={join} filter={filter} />
            <line x1={obj.x1} y1={obj.y1} x2={obj.x2} y2={obj.y2} stroke="transparent" strokeWidth={Math.max(12, obj.strokeWidth + 8)} />
          </g>
        );
        break;
      case 'arrow':
        element = (
          <g {...common}>
            <line x1={obj.x1} y1={obj.y1} x2={obj.x2} y2={obj.y2}
              stroke={obj.stroke} strokeWidth={obj.strokeWidth} strokeDasharray={dash} strokeLinejoin={join}
              markerEnd={`url(#marker-${obj.id})`} filter={filter} />
            <line x1={obj.x1} y1={obj.y1} x2={obj.x2} y2={obj.y2} stroke="transparent" strokeWidth={Math.max(12, obj.strokeWidth + 8)} />
            {obj.label && <text x={(obj.x1 + obj.x2) / 2} y={(obj.y1 + obj.y2) / 2 - 8} fontSize="11" fill={obj.stroke} fontFamily="IBM Plex Sans" textAnchor="middle">{obj.label}</text>}
          </g>
        );
        break;
      case 'connector-straight':
      case 'connector-curved':
      case 'connector-elbow': {
        const d = getConnectorPath(obj.type, obj.x1, obj.y1, obj.x2, obj.y2);
        element = (
          <g {...common}>
            <path d={d} fill="none" stroke={obj.stroke} strokeWidth={obj.strokeWidth} strokeDasharray={dash} markerEnd={`url(#marker-${obj.id})`} filter={filter} />
            <path d={d} fill="none" stroke="transparent" strokeWidth={Math.max(12, obj.strokeWidth + 8)} />
            {obj.label && <text x={(obj.x1 + obj.x2) / 2} y={(obj.y1 + obj.y2) / 2 - 8} fontSize="11" fill={obj.stroke} fontFamily="IBM Plex Sans" textAnchor="middle">{obj.label}</text>}
          </g>
        );
        break;
      }
      case 'path':
        element = (
          <g {...common}>
            <path d={obj.d} fill={fill} stroke={obj.stroke} strokeWidth={obj.strokeWidth}
              strokeDasharray={dash} strokeLinecap={cap} strokeLinejoin={join} filter={filter} clipPath={clipPath} />
            <path d={obj.d} fill="none" stroke="transparent" strokeWidth={Math.max(12, (obj.strokeWidth || 0) + 8)} />
          </g>
        );
        break;
      case 'polygon':
        element = (
          <polygon points={obj.points} fill={fill} stroke={obj.stroke} strokeWidth={obj.strokeWidth}
            strokeLinejoin={join} filter={filter} clipPath={clipPath}
            transform={obj.rotation ? `rotate(${obj.rotation} ${obj.x + obj.w / 2} ${obj.y + obj.h / 2})` : undefined}
            {...common} />
        );
        break;
      case 'artboard':
        element = (
          <g {...common}>
            <rect x={obj.x} y={obj.y} width={obj.w} height={obj.h} fill="#FFFFFF" stroke="#CBD5E1" strokeWidth={1.5} />
            <text x={obj.x} y={obj.y - 8} fontSize="14" fill="#0B1D3A" fontFamily="IBM Plex Sans" fontWeight="600">{obj.label || ''}</text>
          </g>
        );
        break;
      case 'group':
        element = <g {...common} filter={filter}>{(obj.children || []).map(c => renderObject(c))}</g>;
        break;
      default: return null;
    }

    return <g key={obj.id}>{element}</g>;
  };

  const renderSelectionHandles = () => {
    if (selectedIds.length === 0) return null;
    const hs = HANDLE_SIZE / zoom;
    return selectedIds.map(id => {
      const obj = objects.find(o => o.id === id);
      if (!obj) return null;
      const bbox = getBBox(obj);
      const handles = [
        { pos: 'nw', x: bbox.x, y: bbox.y }, { pos: 'ne', x: bbox.x + bbox.w, y: bbox.y },
        { pos: 'sw', x: bbox.x, y: bbox.y + bbox.h }, { pos: 'se', x: bbox.x + bbox.w, y: bbox.y + bbox.h },
        { pos: 'n', x: bbox.x + bbox.w / 2, y: bbox.y }, { pos: 's', x: bbox.x + bbox.w / 2, y: bbox.y + bbox.h },
        { pos: 'w', x: bbox.x, y: bbox.y + bbox.h / 2 }, { pos: 'e', x: bbox.x + bbox.w, y: bbox.y + bbox.h / 2 },
      ];
      const cursors = { nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize', n: 'n-resize', s: 's-resize', w: 'w-resize', e: 'e-resize' };
      return (
        <g key={`sel-${id}`}>
          <rect x={bbox.x} y={bbox.y} width={bbox.w} height={bbox.h}
            fill="none" stroke="#0D9488" strokeWidth={1.2 / zoom} strokeDasharray={`${3 / zoom} ${2 / zoom}`} />
          {handles.map(h => (
            <rect key={h.pos} x={h.x - hs / 2} y={h.y - hs / 2} width={hs} height={hs}
              fill="white" stroke="#0D9488" strokeWidth={1.2 / zoom} rx={1 / zoom}
              style={{ cursor: cursors[h.pos] }}
              onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, 'resize', h.pos, id); }} />
          ))}
          <line x1={bbox.x + bbox.w / 2} y1={bbox.y} x2={bbox.x + bbox.w / 2} y2={bbox.y - 24 / zoom}
            stroke="#0D9488" strokeWidth={1 / zoom} />
          <circle cx={bbox.x + bbox.w / 2} cy={bbox.y - 24 / zoom} r={4 / zoom}
            fill="white" stroke="#0D9488" strokeWidth={1.2 / zoom} style={{ cursor: 'grab' }}
            onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, 'rotate', null, id); }} />
        </g>
      );
    });
  };

  // Direct selection: show anchor points for paths
  const renderDirectSelectionHandles = () => {
    if (tool !== TOOLS.DIRECT_SELECT || !directSelectPoints || directSelectPoints.length === 0) return null;
    const ps = 4 / zoom;
    return directSelectPoints.map((pt, i) => {
      if (pt.type === 'close') return null;
      const isActive = directSelectIdx === i;
      return (
        <g key={`dsp-${i}`}>
          {pt.cx1 !== undefined && (
            <>
              <line x1={pt.x} y1={pt.y} x2={pt.cx1} y2={pt.cy1} stroke="#3B82F6" strokeWidth={0.8 / zoom} />
              <circle cx={pt.cx1} cy={pt.cy1} r={ps * 0.7} fill="#3B82F6" stroke="white" strokeWidth={0.5 / zoom} style={{ cursor: 'pointer' }}
                onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, 'direct-handle', `cx1-${i}`, null); }} />
            </>
          )}
          {pt.cx2 !== undefined && (
            <>
              <line x1={pt.x} y1={pt.y} x2={pt.cx2} y2={pt.cy2} stroke="#3B82F6" strokeWidth={0.8 / zoom} />
              <circle cx={pt.cx2} cy={pt.cy2} r={ps * 0.7} fill="#3B82F6" stroke="white" strokeWidth={0.5 / zoom} style={{ cursor: 'pointer' }}
                onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, 'direct-handle', `cx2-${i}`, null); }} />
            </>
          )}
          <rect x={pt.x - ps / 2} y={pt.y - ps / 2} width={ps} height={ps}
            fill={isActive ? '#3B82F6' : 'white'} stroke="#3B82F6" strokeWidth={1.2 / zoom}
            style={{ cursor: 'pointer' }}
            transform={`rotate(45 ${pt.x} ${pt.y})`}
            onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, 'direct-point', i, null); }} />
        </g>
      );
    });
  };

  const renderPenPreview = () => {
    if ((tool !== TOOLS.PEN && tool !== TOOLS.CURVATURE) || penPoints.length === 0) return null;
    const pts = penPoints;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const pt = pts[i];
      const hasPrevOut = prev.cx2 !== undefined && prev.cy2 !== undefined;
      const hasCurrIn = pt.cx1 !== undefined && pt.cy1 !== undefined;
      if (hasPrevOut || hasCurrIn) {
        const c1x = hasPrevOut ? prev.cx2 : prev.x;
        const c1y = hasPrevOut ? prev.cy2 : prev.y;
        const c2x = hasCurrIn ? pt.cx1 : pt.x;
        const c2y = hasCurrIn ? pt.cy1 : pt.y;
        d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${pt.x} ${pt.y}`;
      } else {
        d += ` L ${pt.x} ${pt.y}`;
      }
    }
    const handleSz = 3 / zoom;
    const lineW = 0.8 / zoom;
    return (
      <g>
        <path d={d} fill="none" stroke={currentStyle.stroke} strokeWidth={currentStyle.strokeWidth} opacity={0.7} />
        <path d={d} fill="none" stroke="#0D9488" strokeWidth={1.5 / zoom} strokeDasharray={`${4/zoom} ${2/zoom}`} />
        {pts.map((pt, i) => (
          <g key={i}>
            {/* Handle lines and circles */}
            {pt.cx1 !== undefined && <>
              <line x1={pt.x} y1={pt.y} x2={pt.cx1} y2={pt.cy1} stroke="#0D9488" strokeWidth={lineW} strokeDasharray={`${2/zoom}`} />
              <circle cx={pt.cx1} cy={pt.cy1} r={handleSz} fill="white" stroke="#0D9488" strokeWidth={lineW} />
            </>}
            {pt.cx2 !== undefined && <>
              <line x1={pt.x} y1={pt.y} x2={pt.cx2} y2={pt.cy2} stroke="#0D9488" strokeWidth={lineW} strokeDasharray={`${2/zoom}`} />
              <circle cx={pt.cx2} cy={pt.cy2} r={handleSz} fill="white" stroke="#0D9488" strokeWidth={lineW} />
            </>}
            {/* Anchor point: circle for smooth, diamond for corner */}
            {(pt.cx1 !== undefined || pt.cx2 !== undefined) ? (
              <circle cx={pt.x} cy={pt.y} r={4 / zoom} fill="white" stroke="#0D9488" strokeWidth={1.5 / zoom} />
            ) : (
              <rect x={pt.x - 3.5/zoom} y={pt.y - 3.5/zoom} width={7/zoom} height={7/zoom}
                fill="white" stroke="#0D9488" strokeWidth={1.5 / zoom}
                transform={`rotate(45 ${pt.x} ${pt.y})`} />
            )}
          </g>
        ))}
      </g>
    );
  };

  const renderSnapGuides = () => {
    if (!snapGuides || snapGuides.length === 0) return null;
    return snapGuides.map((g, i) =>
      g.type === 'v'
        ? <line key={`sg-${i}`} x1={g.pos} y1={-5000} x2={g.pos} y2={5000} stroke="#EF4444" strokeWidth={0.8 / zoom} strokeDasharray={`${3 / zoom} ${2 / zoom}`} />
        : <line key={`sg-${i}`} x1={-5000} y1={g.pos} x2={5000} y2={g.pos} stroke="#EF4444" strokeWidth={0.8 / zoom} strokeDasharray={`${3 / zoom} ${2 / zoom}`} />
    );
  };

  const renderRubberBand = () => {
    if (!rubberBand) return null;
    return <rect x={rubberBand.x} y={rubberBand.y} width={rubberBand.w} height={rubberBand.h}
      fill="rgba(13,148,136,0.08)" stroke="#0D9488" strokeWidth={1 / zoom} strokeDasharray={`${3 / zoom} ${2 / zoom}`} />;
  };

  const renderLasso = () => {
    if (!lassoPoints || lassoPoints.length < 2) return null;
    const d = lassoPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    return <path d={d} fill="rgba(59,130,246,0.06)" stroke="#3B82F6" strokeWidth={1.2 / zoom} strokeDasharray={`${4 / zoom} ${2 / zoom}`} />;
  };

  // Text on path: render path defs
  const textPathDefs = useMemo(() => {
    return objects.filter(o => o.type === 'text' && o.textPathId).map(o => {
      const pathObj = objects.find(p => p.id === o.textPathId);
      if (!pathObj) return null;
      let d = '';
      if (pathObj.type === 'path') d = pathObj.d;
      else if (pathObj.type === 'line') d = `M ${pathObj.x1} ${pathObj.y1} L ${pathObj.x2} ${pathObj.y2}`;
      else if (pathObj.type === 'ellipse') {
        const cx = pathObj.x + pathObj.w / 2, cy = pathObj.y + pathObj.h / 2;
        const rx = pathObj.w / 2, ry = pathObj.h / 2;
        d = `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx - rx} ${cy}`;
      }
      return d ? <path key={`textpath-${o.textPathId}`} id={`textpath-${o.textPathId}`} d={d} fill="none" /> : null;
    }).filter(Boolean);
  }, [objects]);

  const markers = objects.filter(o => o.type === 'arrow' || o.type?.startsWith('connector')).map(renderArrowMarker);

  // ========== Anchor Point Tool overlay ==========
  const renderAnchorPoints = () => {
    if (tool !== TOOLS.ANCHOR_POINT || !anchorPoints || anchorPoints.length === 0) return null;
    const sz = 5 / zoom;
    const handleSz = 4 / zoom;
    const lineW = 1 / zoom;
    return (
      <g className="anchor-overlay">
        {anchorPoints.map((pt, i) => {
          const hasHandles = pt.cx1 !== undefined || pt.cx2 !== undefined;
          return (
            <g key={i}>
              {/* Handle lines */}
              {pt.cx1 !== undefined && (
                <>
                  <line x1={pt.x} y1={pt.y} x2={pt.cx1} y2={pt.cy1} stroke="#0D9488" strokeWidth={lineW} strokeDasharray={`${2/zoom}`} />
                  <circle
                    cx={pt.cx1} cy={pt.cy1} r={handleSz}
                    fill="white" stroke="#0D9488" strokeWidth={lineW}
                    style={{ cursor: 'pointer' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const svg = e.target.closest('svg');
                      const ctm = svg.getScreenCTM().inverse();
                      const onMove = (me) => {
                        const svgPt = svg.createSVGPoint();
                        svgPt.x = me.clientX;
                        svgPt.y = me.clientY;
                        const transformed = svgPt.matrixTransform(ctm);
                        onAnchorHandleDrag(i, 'in', transformed.x, transformed.y);
                      };
                      const onUp = () => {
                        window.removeEventListener('mousemove', onMove);
                        window.removeEventListener('mouseup', onUp);
                      };
                      window.addEventListener('mousemove', onMove);
                      window.addEventListener('mouseup', onUp);
                    }}
                  />
                </>
              )}
              {pt.cx2 !== undefined && (
                <>
                  <line x1={pt.x} y1={pt.y} x2={pt.cx2} y2={pt.cy2} stroke="#0D9488" strokeWidth={lineW} strokeDasharray={`${2/zoom}`} />
                  <circle
                    cx={pt.cx2} cy={pt.cy2} r={handleSz}
                    fill="white" stroke="#0D9488" strokeWidth={lineW}
                    style={{ cursor: 'pointer' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const svg = e.target.closest('svg');
                      const ctm = svg.getScreenCTM().inverse();
                      const onMove = (me) => {
                        const svgPt = svg.createSVGPoint();
                        svgPt.x = me.clientX;
                        svgPt.y = me.clientY;
                        const transformed = svgPt.matrixTransform(ctm);
                        onAnchorHandleDrag(i, 'out', transformed.x, transformed.y);
                      };
                      const onUp = () => {
                        window.removeEventListener('mousemove', onMove);
                        window.removeEventListener('mouseup', onUp);
                      };
                      window.addEventListener('mousemove', onMove);
                      window.addEventListener('mouseup', onUp);
                    }}
                  />
                </>
              )}
              {/* Anchor point (diamond for corner, circle for smooth) — drag to pull handles */}
              {hasHandles ? (
                <circle
                  cx={pt.x} cy={pt.y} r={sz}
                  fill={anchorSelectedIdx === i ? '#0D9488' : 'white'}
                  stroke="#0D9488" strokeWidth={lineW * 1.5}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    const svg = e.target.closest('svg');
                    const ctm = svg.getScreenCTM().inverse();
                    let dragged = false;
                    const onMove = (me) => {
                      dragged = true;
                      const svgPt = svg.createSVGPoint();
                      svgPt.x = me.clientX;
                      svgPt.y = me.clientY;
                      const t = svgPt.matrixTransform(ctm);
                      onAnchorPointDrag(i, t.x, t.y);
                    };
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                      if (!dragged) onAnchorClick(i);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                  data-testid={`anchor-point-${i}`}
                />
              ) : (
                <rect
                  x={pt.x - sz} y={pt.y - sz} width={sz * 2} height={sz * 2}
                  fill={anchorSelectedIdx === i ? '#0D9488' : 'white'}
                  stroke="#0D9488" strokeWidth={lineW * 1.5}
                  transform={`rotate(45 ${pt.x} ${pt.y})`}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    const svg = e.target.closest('svg');
                    const ctm = svg.getScreenCTM().inverse();
                    let dragged = false;
                    const onMove = (me) => {
                      dragged = true;
                      const svgPt = svg.createSVGPoint();
                      svgPt.x = me.clientX;
                      svgPt.y = me.clientY;
                      const t = svgPt.matrixTransform(ctm);
                      onAnchorPointDrag(i, t.x, t.y);
                    };
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                      if (!dragged) onAnchorClick(i);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                  data-testid={`anchor-point-${i}`}
                />
              )}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <svg ref={svgRef} width="100%" height="100%" viewBox={viewBox}
      onMouseDown={(e) => onCanvasClick(e)}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      onWheel={onWheel} onContextMenu={onContextMenu}
      data-testid="figure-canvas"
      style={{ background: '#F8FAFC', cursor: tool === TOOLS.PAN ? 'grab' : tool === TOOLS.LASSO ? 'crosshair' : tool === TOOLS.ANCHOR_POINT ? 'crosshair' : tool === TOOLS.SELECT || tool === TOOLS.DIRECT_SELECT ? 'default' : 'crosshair' }}
      className="figure-canvas-svg">
      <defs>
        {markers}
        {gradientDefs}
        {filterDefs}
        {clipDefs}
        {textPathDefs}
      </defs>
      {renderGrid()}
      {renderRulers()}
      <rect x={0} y={0} width={800} height={600} fill="white" stroke="#E2E8F0" strokeWidth={1} />
      {objects.map(renderObject)}
      {renderSelectionHandles()}
      {renderDirectSelectionHandles()}
      {renderPenPreview()}
      {renderSnapGuides()}
      {renderRubberBand()}
      {renderLasso()}
      {renderAnchorPoints()}
    </svg>
  );
};

export default React.memo(EditorCanvas);
